import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor, Badge, Box, Card, Divider, Group, Stack, Text, Title } from '@mantine/core';
import { IconArrowLeft, IconCircleCheck } from '@tabler/icons-react';
import DealFinalSettlement from '@/components/app/deals/DealFinalSettlement';
import NoNds from '@/components/common/NoNds';
import ForwardingWeights from '@/components/app/deals/steps/ForwardingWeights';
import InvoicesReadonly from '@/components/app/deals/steps/InvoicesReadonly';
import CarrierAssignSummary from '@/components/app/deals/steps/CarrierAssignSummary';
import StepDocList from '@/components/app/deals/StepDocList';
import { isoToRu } from '@/lib/date';
import { visibleInvoices } from '@/lib/dealInvoices';
import { dealStepKey, documentsForStep, type DealStepKey } from '@/lib/dealSteps';
import { formatMoney, formatOrgName } from '@/lib/format';
import { schedulePeriod } from '@/lib/schedule';
import type { BargainStep, BargainStepsInfo } from '@/types/deals';
import IdText from '@/components/common/IdText';

interface DealStepReadonlyProps {
  readonly info: BargainStepsInfo;
  readonly step: BargainStep;
  readonly onBackToCurrent: () => void;
}

function Row({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <Box>
      <Text fz="xs" c="gray.7" mb={2}>
        {label}
      </Text>
      <Box fz="sm">{children}</Box>
    </Box>
  );
}

// Read-only сводка пройденного шага: что было сделано (по долговременным данным снапшота) + документы
// шага. Интерактивных действий нет — прошлые шаги неизменяемы. Текущий/будущие шаги сюда не попадают.
export default function DealStepReadonly({ info, step, onBackToCurrent }: DealStepReadonlyProps) {
  const { t } = useTranslation();
  const key = dealStepKey(step);
  // Для «Перевозки» документы уже показаны организованно внутри body (ForwardingWeights: РТС в
  // «Разобранном», доки дня в «Отгружено/Доставлено», УПД в «Подпись УПД», ТТН/ТРН в карточке ТС) —
  // плоский повтор в «Документах шага» был бы дублем. Для остальных шагов показываем доки шага.
  // «Перевозка» и «Окончательный расчёт» показывают свои документы внутри body (ForwardingWeights /
  // DealFinalSettlement) — плоский повтор в «Документах шага» был бы дублем. «Счета» — аналогично:
  // документы счетов встроены в строки InvoicesReadonly.
  const docs =
    key === 'forwarding' || key === 'finalPayment' || key === 'invoices' || key === 'carrierAssign'
      ? []
      : documentsForStep(info.documents, key);
  const body = stepBody(key, info, t);

  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" wrap="nowrap" mb="md">
        <Group gap="xs" wrap="nowrap">
          <Title order={5}>{step.name}</Title>
          <Badge variant="light" color="lime" leftSection={<IconCircleCheck size={12} />} tt="none" radius="sm">
            {t('app.deals.readonly.completed')}
          </Badge>
        </Group>
        <Anchor component="button" type="button" fz="sm" onClick={onBackToCurrent} underline="never" c="brand.7">
          <Group gap={4} wrap="nowrap">
            <IconArrowLeft size={14} />
            {t('app.deals.readonly.backToCurrent')}
          </Group>
        </Anchor>
      </Group>

      <Stack gap="md">
        {body}

        {docs.length > 0 ? (
          <Stack gap="xs">
            {body ? <Divider /> : null}
            <Text fz="sm" fw={600} c="gray.8">
              {t('app.deals.readonly.documents')}
            </Text>
            <StepDocList documents={docs} />
          </Stack>
        ) : null}

        {!body && docs.length === 0 ? (
          <Text fz="sm" c="gray.6">
            {t('app.deals.readonly.noData')}
          </Text>
        ) : null}
      </Stack>
    </Card>
  );
}

type T = ReturnType<typeof useTranslation>['t'];

// Богатая часть сводки по типу шага (из долговременных полей снапшота). null → только документы/заглушка.
function stepBody(key: DealStepKey, info: BargainStepsInfo, t: T): ReactNode {
  switch (key) {
    case 'attestation':
      return info.bargainStatus === 'CANCELED' ? null : (
        <Text fz="sm" c="gray.8">
          {t('app.deals.readonly.attestationDone')}
        </Text>
      );
    case 'invoices':
      // Продавцу — надпись (счетов не показываем), остальным — сводка при наличии счетов.
      return info.isSeller === true || visibleInvoices(info).length > 0 ? <InvoicesReadonly info={info} /> : null;
    case 'shipment':
      return shipmentBody(info, t);
    case 'schedule':
      return scheduleBody(info, t);
    case 'consignee':
      return consigneeBody(info, t);
    case 'forwarderAssign':
      return forwarderAssignBody(info, t);
    case 'forwarderInstruction':
      return forwarderInstructionBody(info, t);
    case 'carrierAssign':
      // Шаг появился позже части сделок: у них данных о перевозчике нет вовсе. Пустая карточка под
      // отметкой «Выполнен» выглядела бы потерей данных — честнее сказать, почему сводки нет.
      return info.carrierAssignedAt ? (
        <CarrierAssignSummary info={info} />
      ) : (
        <Text fz="sm" c="gray.7">
          {t('app.deals.carrierAssign.noHistory')}
        </Text>
      );
    case 'forwarding':
      return info.forwardingInfo ? <ForwardingWeights info={info} /> : null;
    case 'finalPayment':
      return <DealFinalSettlement info={info} />;
    default:
      return null;
  }
}

function partyLabel(code: string | undefined, t: T): string {
  if (code === 'CUSTOMER') {
    return t('app.deals.summary.buyer');
  }
  if (code === 'SELLER') {
    return t('app.deals.summary.seller');
  }
  return code ?? '—';
}

function shipmentBody(info: BargainStepsInfo, t: T): ReactNode {
  const start = info.shipmentSchedule?.start;
  return (
    <Stack gap="md">
      <Text fz="sm" c="gray.8">
        {t('app.deals.readonly.shipmentConfirmed')}
      </Text>
      {start ? (
        <Group gap="xl" wrap="wrap">
          <Row label={t('app.deals.readonly.shipmentStart')}>{start}</Row>
        </Group>
      ) : null}
    </Stack>
  );
}

function scheduleBody(info: BargainStepsInfo, t: T): ReactNode {
  const schedule = info.shipmentSchedule;
  if (!schedule?.start && !schedule?.confirmedBy) {
    return null;
  }
  // Последний день берём из выбранных дат (поле end бэка часто пустое).
  const lastDay = schedulePeriod(schedule)?.endIso;
  return (
    <Group gap="xl" wrap="wrap">
      {schedule.start ? <Row label={t('app.deals.readonly.shipmentStart')}>{schedule.start}</Row> : null}
      {lastDay ? <Row label={t('app.deals.readonly.shipmentLast')}>{isoToRu(lastDay)}</Row> : null}
      {schedule.requiredDays != null ? (
        <Row label={t('app.deals.readonly.shipmentDays')}>{schedule.requiredDays}</Row>
      ) : null}
      {schedule.confirmedBy ? (
        <Row label={t('app.deals.readonly.confirmedBy')}>{partyLabel(schedule.confirmedBy, t)}</Row>
      ) : null}
    </Group>
  );
}

// «Выбор грузополучателя» — только грузополучатель. Экспедитор здесь НЕ показывается: он назначается
// на следующей стадии, его реквизиты — в сводке «Назначение экспедитора» (forwarderAssignBody).
function consigneeBody(info: BargainStepsInfo, t: T): ReactNode {
  const consignee = info.consignee;
  if (!consignee?.name) {
    return null;
  }
  return (
    <Group gap="xl" wrap="wrap" align="flex-start">
      <Row label={t('app.deals.readonly.consignee')}>
        <Text fz="sm" fw={500}>
          {formatOrgName(consignee.name)}
        </Text>
        {consignee.inn ? (
          <Text fz="sm" c="gray.7">
            {t('auth.fields.inn')} <IdText>{consignee.inn}</IdText>
          </Text>
        ) : null}
        {consignee.address ? (
          <Text fz="xs" c="gray.7">
            {consignee.address}
          </Text>
        ) : null}
      </Row>
    </Group>
  );
}

// Сводка завершённой стадии «Назначение экспедитора»: кто назначен (ИП/организация + ИНН + ставка) и
// когда (дата/время принятия заявки экспедитором).
function forwarderAssignBody(info: BargainStepsInfo, t: T): ReactNode {
  const forwarder = info.forwarder;
  if (!forwarder?.name && !forwarder?.inn) {
    return null;
  }
  return (
    <Stack gap="md">
      <Text fz="sm" c="gray.8">
        {t('app.deals.readonly.forwarderAssigned')}
      </Text>
      <Group gap="xl" wrap="wrap" align="flex-start">
        <Row label={t('app.deals.readonly.forwarder')}>
          {forwarder.name ? (
            <Text fz="sm" fw={500}>
              {formatOrgName(forwarder.name)}
            </Text>
          ) : null}
          {forwarder.inn ? (
            <Text fz="sm" c="gray.7">
              {t('auth.fields.inn')} <IdText>{forwarder.inn}</IdText>
            </Text>
          ) : null}
          {/* Стоимость экспедирования — только агенту и покупателю; продавцу её не показываем. */}
          {forwarder.feePerTon != null && info.isSeller !== true ? (
            <Text fz="sm" c="gray.7">
              {formatMoney(forwarder.feePerTon)} {t('app.deals.rubPerTon')}
              <NoNds />
            </Text>
          ) : null}
        </Row>
        {info.forwarderAssignedAt ? (
          <Row label={t('app.deals.readonly.forwarderAssignedAt')}>{info.forwarderAssignedAt}</Row>
        ) : null}
      </Group>
    </Stack>
  );
}

// Сводка завершённой стадии «Поручение экспедитора»: поручение подписано агентом и передано
// экспедитору, плюс для кого оно (ИП/организация + ИНН экспедитора). Стоимость здесь не показываем —
// она относится к сводке назначения.
function forwarderInstructionBody(info: BargainStepsInfo, t: T): ReactNode {
  const forwarder = info.forwarder;
  return (
    <Stack gap="md">
      <Text fz="sm" c="gray.8">
        {t('app.deals.readonly.forwarderInstructionDone')}
      </Text>
      {forwarder?.name || forwarder?.inn ? (
        <Group gap="xl" wrap="wrap" align="flex-start">
          <Row label={t('app.deals.readonly.forwarder')}>
            {forwarder.name ? (
              <Text fz="sm" fw={500}>
                {formatOrgName(forwarder.name)}
              </Text>
            ) : null}
            {forwarder.inn ? (
              <Text fz="sm" c="gray.7">
                {t('auth.fields.inn')} <IdText>{forwarder.inn}</IdText>
              </Text>
            ) : null}
          </Row>
        </Group>
      ) : null}
    </Stack>
  );
}

